import { Link } from 'react-router-dom';
import { NOTIFICATION_ROUTES } from '@/api/constants/app.url';
import { useNotificationsQuery } from '@/modules/notifications/hooks/use-notifications-query';
import { useMarkAllReadMutation } from '@/modules/notifications/hooks/use-mark-all-read-mutation';
import { NotificationItem } from './notification-item';

interface Props {
    /** Close the dropdown — called on item navigation and "View all". */
    onClose: () => void;
}

const DROPDOWN_LIMIT = 8;

/** The bell dropdown body — recent notifications + mark-all-read + "View all". */
export function NotificationDropdown({ onClose }: Props) {
    const { data, isLoading } = useNotificationsQuery({ limit: DROPDOWN_LIMIT });
    const markAll = useMarkAllReadMutation();

    const notifications = data?.notifications ?? [];
    const unread = data?.unread_count ?? 0;

    return (
        <div className="absolute right-0 top-full mt-2 w-96 overflow-hidden rounded-xl2 border border-ink-100 bg-white shadow-pop">
            <div className="flex items-center justify-between border-b border-ink-100 p-3">
                <h4 className="font-display text-sm font-semibold text-brand-navy">Notifications</h4>
                {unread > 0 && (
                    <button
                        type="button"
                        onClick={() => markAll.mutate()}
                        disabled={markAll.isPending}
                        className="font-ui text-xs font-semibold text-brand-sky-500 transition hover:text-brand-sky disabled:opacity-50"
                    >
                        Mark all read
                    </button>
                )}
            </div>

            <div className="max-h-96 divide-y divide-ink-100 overflow-y-auto">
                {isLoading ? (
                    <div className="px-4 py-6 text-center text-sm text-ink-500">Loading…</div>
                ) : notifications.length === 0 ? (
                    <div className="px-4 py-8 text-center text-sm text-ink-500">You're all caught up.</div>
                ) : (
                    notifications.map((n) => (
                        <NotificationItem key={n.id} notification={n} onNavigate={onClose} />
                    ))
                )}
            </div>

            <Link
                to={NOTIFICATION_ROUTES.list}
                onClick={onClose}
                className="block border-t border-ink-100 px-4 py-3 text-center font-ui text-xs font-semibold text-brand-sky-500 transition hover:bg-ink-50"
            >
                View all notifications
            </Link>
        </div>
    );
}
